
import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { TranslateService } from '../../../../../services/translate.service';
import { DirectionService } from '../../../../../services/direction.service';
import { MaintainAccountService } from '../../../maintain-account/maintain-account.service';
import { AjaxService } from '../../../../../services/ajax.service';
import { ViewScopeService } from '../../../../../services/view-scope.service';
import { MAIN_API_DEFS } from '../../../../../services/account-api.constants';

// PrimeNG Modules
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { AutoComplete } from 'primeng/autocomplete';
import { DropdownModule } from 'primeng/dropdown';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';


// Interface for a connected entity (Customer or Supplier)
export interface ConnectedEntity {
    id: string; // Unique ID of the entity
    businessId: string;
    businessName: string;
}

// Interface for a relation entry
export interface Relation {
    id: string;
    recId?: number;
    UserRecID: string;
    connectedEntityId: string;       // ID of the connected Customer/Supplier
    connectedEntityDisplay: string;  // For displaying in the table (e.g., "ID - Name")
    customer_supplier_number: string;
    innerNumber: string;
    description: string;
    isActive: number;
    isApproved: number;

    // New fields to store selected partner info
    partnerRecId?: number;
    partnerBusinessId?: string;
    connectedPartner?: any; // The new property to hold the selected object
}


// Interface for dropdown options
interface SelectItem {
    label: string;
    value: string;
    data: ConnectedEntity; // Store the full entity data
}

@Component({
    selector: 'app-relations',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        InputTextModule,
        ButtonModule,
        ToastModule,
        TagModule,
        TableModule,
        AutoComplete,
        DropdownModule
    ],
    templateUrl: './relations.component.html',
    styleUrls: ['./relations.component.scss'],
    providers: [MessageService]
})
export class RelationsComponent implements OnInit {

    @Input() entityType: 'Customer' | 'Supplier' | null = null;
    @Input() relations: Relation[] = [];

    @Output() relationsChange = new EventEmitter<Relation[]>();
    @Output() nextStep = new EventEmitter<void>();
    @Output() prevStep = new EventEmitter<void>();

    @Input() customerRecId!: number;

    filteredPartners: any[] = [];
    allPartners: any[] = [];

    readonly DESCRIPTION_MAX_LENGTH = 250;

    isDialogMode = false;
    isCreate = false;
    newEntry: Relation = {
        id: '',
        recId: 0,
        UserRecID: '',
        customer_supplier_number: '',
        connectedEntityId: '',
        connectedEntityDisplay: '',
        isActive: 1,
        isApproved: 1,
        innerNumber: '',
        description: ''
    };


    constructor(private messageService: MessageService,
        public translateServ: TranslateService,
        public dirServ: DirectionService,
        private ajaxService: AjaxService,
        private viewScopeService: ViewScopeService,
        @Optional() public ref: DynamicDialogRef,
        @Optional() public config: DynamicDialogConfig,
        @Optional() private maintainAccountService: MaintainAccountService) { }

    ngOnInit(): void {
        if (this.config && this.config.data) {
            this.isDialogMode = this.config.data.isDialog;
            this.customerRecId = this.config.data.customerRecId;
            this.isCreate = this.config.data.isCreate;
            this.entityType = this.config.data.entityType; // Ensure entityType is set from dialog data

            if (!this.isCreate && this.config.data.nameEntry) {
                this.relations = [this.config.data.nameEntry];
            } else {
                this.relations = [];
            }
        }
        else {
            this.isDialogMode = false;
            this.isCreate = true;

            this.relations.forEach(item => {
                if (!item.id) {
                    item.id = this.generateUniqueId();
                }
            });
            this.resetNewEntry();
        }
        this.loadPartners();
    }



    private generateUniqueId(): string {
        return 'rel_' + Math.random().toString(36).substring(2, 9);
    }

    resetNewEntry(): void {
        this.newEntry = {
            id: this.generateUniqueId(),
            UserRecID: '',
            customer_supplier_number: '',
            connectedEntityId: '',
            connectedEntityDisplay: '',
            isActive: 1,
            isApproved: 1,
            recId: 0,
            innerNumber: '',
            description: '',
            connectedPartner: null
        };
    }



    // The event from p-autoComplete on a row selection
    onConnectedEntitySelected(selected: any): void {
        if (selected) {
            this.newEntry.connectedEntityId = selected.value.RecID ?? '';
            this.newEntry.customer_supplier_number = selected.value.customer_supplier_num ?? '';
            this.newEntry.UserRecID = this.viewScopeService.getCurrentOpertaingUserId().toString() ?? '';
            this.newEntry.connectedEntityDisplay = selected.value.label;
            this.newEntry.partnerRecId = selected.value.RecID ?? null;
            this.newEntry.partnerBusinessId = selected.value.Business_ID ?? null;
        } else {
            this.newEntry.connectedEntityId = '';
            this.newEntry.connectedEntityDisplay = '';
            this.newEntry.partnerRecId = undefined;
            this.newEntry.partnerBusinessId = undefined;
            this.newEntry.customer_supplier_number = '';
        }
    }

    getSeverity(val: boolean): string {
        return val ? 'success' : 'danger';
    }

    addEntry(): void {
        if (!this.newEntry.connectedEntityId) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Connected Entity is required.',
                life: 3000
            });
            return;
        }
        if (!this.newEntry.innerNumber.trim()) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Inner Number is required.',
                life: 3000
            });
            return;
        }
        if (this.newEntry.description.length > this.DESCRIPTION_MAX_LENGTH) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: `Description exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                life: 3000
            });
            return;
        }

        const isDuplicate = this.relations.some(
            rel => rel.connectedEntityId === this.newEntry.connectedEntityId
        );

        if (isDuplicate) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Duplicate Entry',
                detail: 'This entity is already connected.',
                life: 3000
            });
            return;
        }

        this.relations.push({ ...this.newEntry });
        this.emitChanges();
        if (this.isCreate) {
            this.resetNewEntry();
        }
        this.messageService.add({
            severity: 'success',
            summary: 'Added',
            detail: 'New relation added.',
            life: 1500
        });
    }

    removeEntry(entryToRemove: Relation): void {
        this.relations = this.relations.filter(entry => entry.id !== entryToRemove.id);
        this.emitChanges();
        this.messageService.add({
            severity: 'info',
            summary: 'Removed',
            detail: 'Relation removed.',
            life: 1500
        });
    }

    emitChanges(): void {
        this.relationsChange.emit(this.relations);
    }

    getRemainingChars(description: string): number {
        return this.DESCRIPTION_MAX_LENGTH - description.length;
    }

    validateAndProceed(): void {
        let isValid = true;
        if (this.relations.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Information',
                detail: 'No relations added. You can add relations or proceed.',
                life: 3000
            });
        }

        for (const entry of this.relations) {
            if (!entry.connectedEntityId) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Connected Entity is required for entry "${entry.id}".`,
                    life: 3000
                });
                break;
            }
            if (!entry.innerNumber.trim()) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Inner Number is required for entry "${entry.connectedEntityDisplay || entry.id}".`,
                    life: 3000
                });
                break;
            }
            if (entry.description.length > this.DESCRIPTION_MAX_LENGTH) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Description for "${entry.connectedEntityDisplay || entry.id}" exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                    life: 3000
                });
                break;
            }
        }

        if (isValid) {
            this.nextStep.emit();
            this.messageService.clear();
        }
    }

    goToPreviousStep(): void {
        this.prevStep.emit();
    }

    applyRowChanges(entry: Relation): void {
        this.emitChanges();

        this.messageService.add({
            severity: 'info',
            summary: 'Changes Applied',
            detail: `Changes applied for "${entry.connectedEntityDisplay}". Remember to press Save to persist.`,
            life: 2500
        });
    }

    saveAndSubmit(): void {
        let isValid = true;

        for (const entry of this.relations) {
            if (!entry.connectedEntityId) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Name for entry "${entry.id}" is required.`,
                    life: 3000
                });
                break;
            }
            if (entry.description.length > this.DESCRIPTION_MAX_LENGTH) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Description for "${entry.connectedEntityId}" exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                    life: 3000
                });
                break;
            }
        }

        if (!isValid) return;

        if (this.maintainAccountService) {
            const requests = this.relations.map(entry => {
                const basePayload: any = {
                    SupplierRecID: entry.partnerRecId,
                    BusinessID: entry.connectedEntityId,
                    customer_supplier_num: entry.customer_supplier_number,
                    customer_id: entry.innerNumber,
                    InnerNumber: entry.innerNumber,
                    Descr: entry.description,
                    IsActive: entry.isActive,
                    IsApproved: entry.isApproved,
                    UserRecID: this.viewScopeService.getCurrentOpertaingUserId(),
                };

                if (this.isCreate) {
                    basePayload.CustomerRecID = this.customerRecId;
                } else {
                    basePayload.RecID = entry.id;
                }

                return basePayload;
            });

            console.log("Data we will save:", requests);

            requests.forEach(record => {
                this.maintainAccountService.insertOrUpdateRecord(this.isCreate, this.constructor.name.substring(1), JSON.stringify(record)).subscribe(
                    {
                        next: (response) => {
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Success',
                                detail: `Saved record for "${record.AdditName}".`,
                                life: 2000
                            });

                            if (this.ref && record === requests[requests.length - 1]) {
                                this.ref.close(response);
                            }
                        },
                        error: (error) => {
                            this.messageService.add({
                                severity: 'error',
                                summary: 'Error',
                                detail: `Failed to save record for "${record.AdditName}".`,
                                life: 3000
                            });
                        },
                        complete: () => {
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Complete',
                                detail: 'All records processed.',
                                life: 2000
                            });

                            if (this.ref) {
                                this.ref.close(true);
                            }
                        }
                    });
            });

        } else {
            this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: 'This functionality is not available in standalone mode.',
                life: 3000
            });
        }
    }

    toggleIsActive(entry: any): void {
        entry.isActive = entry.isActive === 1 ? 0 : 1;
    }

    toggleIsApproved(entry: any): void {
        entry.isApproved = entry.isApproved === 1 ? 0 : 1;
    }


    closeDialog(shouldReload: boolean = false): void {
        if (this.ref) {
            this.ref.close(shouldReload);
        }
    }


    searchPartners(event: any): void {
        const query = event.query.toLowerCase();
        this.filteredPartners = this.allPartners.filter(partner =>
            (partner.CustomerName && partner.CustomerName.toLowerCase().includes(query)) ||
            (partner.Business_ID && partner.Business_ID.toLowerCase().includes(query))
        );
    }


    onPartnerSelected(event: any, relation: Relation): void {
        const selected = event.value;
        if (!selected) return;

        relation.connectedPartner = selected;          // Keep full object
        relation.partnerRecId = selected.RecID;        // Internal ID
        relation.partnerBusinessId = selected.Business_ID;
        relation.connectedEntityDisplay = selected.CustomerName; // What user sees
        relation.connectedEntityId = selected.RecID;  // ID to save to backend

        console.log('Selected partner:', relation);
        this.emitChanges();
    }



    private loadPartners(): void {
        const typeCode = this.entityType === 'Customer' ? '100' : '200';
        const url = `${this.ajaxService.getBaseOfApi()}/api/${MAIN_API_DEFS.ALL_CUST_SUP}/${typeCode}/${this.viewScopeService.getCurrentOpertaingUserId()}/${this.viewScopeService.getSelectedCompanyId()}`;

        this.ajaxService.getData(url).subscribe({
            next: (response) => {
                const data = response as any[];
                this.allPartners = data.map(p => ({
                    ...p,
                    label: `${p.Business_ID} - ${p.CustomerName}`
                }));
                this.filteredPartners = [...this.allPartners];
                console.log('Loaded partners:', this.filteredPartners);

                // This is the critical part.
                // It now runs AFTER the partners are loaded.
                if (!this.isCreate && this.relations.length > 0) {
                    const initialPartner = this.allPartners.find(
                        // Finds the partner object using the ID from the incoming data
                        p => p.RecID === this.relations[0].partnerRecId
                    );
                    if (initialPartner) {
                        // Assigns the full object to the relation object
                        this.relations[0].connectedPartner = initialPartner;
                    }
                }
            },
            error: (err: any) => {
                console.error('Failed to load partners', err);
                this.filteredPartners = [];
            }
        });
    }


    private setInitialPartner(): void {
        const initialPartner = this.allPartners.find(
            p => p.RecID === this.relations[0].partnerRecId
        );

        if (initialPartner) {
            // This is what the autocomplete actually displays
            this.newEntry.connectedEntityDisplay = initialPartner.label;

            // Keep the IDs for saving
            this.newEntry.connectedEntityId = initialPartner.RecID;
        }
    }

    // Business logic: If managing a Customer, show 'New Supplier'. If managing a Supplier, show 'New Customer'.
    getNewEntityLabel(): string {
        if (this.entityType === 'Customer') {
            return this.translateServ.dictionary['NEW_SUPPLIER'] || 'New Supplier';
        } else if (this.entityType === 'Supplier') {
            return this.translateServ.dictionary['NEW_CUSTOMER'] || 'New Customer';
        } else {
            return this.translateServ.dictionary['NEW_ENTITY'] || 'New Entity';
        }
    }
}